/**
 * Calin Outfits Viewer — standalone PDP section that mirrors the
 * "Under outfits" tab from the Calin Size Finder.
 * Prefers theme-editor outfit blocks (inline JSON); falls back to
 * assets/calin-size-finder-config.json when no blocks are present.
 */
(function () {
  const OUTFIT_THUMBS = {
    'white-tee': '<rect x="8" y="0" width="32" height="42" rx="3" fill="#f0f0f0" stroke="#ccc"/><path d="M16 0 Q24 6 32 0" fill="none" stroke="#ccc"/>',
    'black-tee': '<rect x="8" y="0" width="32" height="42" rx="3" fill="#2a2a2a" stroke="#111"/>',
    fitted: '<rect x="10" y="0" width="28" height="44" rx="5" fill="#c0a8d8" stroke="#a08ac0"/>',
    'ribbed-tank': '<rect x="12" y="2" width="24" height="42" rx="3" fill="#d4c4a8" stroke="#b8a888"/><line x1="12" y1="10" x2="36" y2="10" stroke="#b8a888"/><line x1="12" y1="20" x2="36" y2="20" stroke="#b8a888"/><line x1="12" y1="30" x2="36" y2="30" stroke="#b8a888"/>',
    'silk-blouse': '<rect x="8" y="2" width="32" height="40" rx="3" fill="#e8d8f0" stroke="#c8b8d8"/>',
    hoodie: '<rect x="6" y="4" width="36" height="40" rx="5" fill="#8aacca" stroke="#6a8caa"/><path d="M16 4 Q24 2 32 4" fill="#7a9cba"/>',
    'button-shirt': '<rect x="8" y="0" width="32" height="44" rx="3" fill="#dde8f0" stroke="#b8ccd8"/><line x1="24" y1="0" x2="24" y2="44" stroke="#b8ccd8"/>',
    'crop-top': '<rect x="10" y="6" width="28" height="28" rx="4" fill="#f4a0b0" stroke="#e08898"/>'
  };

  const CHECK_ICON =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12l5 5L20 7"/></svg>';

  class CalinOutfitsViewer extends HTMLElement {
    constructor() {
      super();
      this.state = { outfitIdx: 0 };
      this._config = null;
    }

    connectedCallback() {
      this.init();
    }

    async init() {
      const config = await this.getConfig();
      if (!config || !config.outfits || !Array.isArray(config.outfits.items) || !config.outfits.items.length) {
        return;
      }
      this.outfits = {
        ...config.outfits,
        items: this._normalizeItems(config.outfits.items)
      };
      this.render();
      this.renderOutfits();
    }

    async getConfig() {
      if (this._config) return this._config;

      let base = null;
      const url = this.dataset.configUrl;
      if (url) {
        try {
          const res = await fetch(url);
          base = await res.json();
        } catch (e) {
          console.warn('Calin Outfits: could not load config', e);
        }
      }

      const inline = this.querySelector('[data-calin-outfits-config]');
      if (inline) {
        try {
          const overrides = JSON.parse(inline.textContent);
          base = base ? this._deepMerge(base, overrides) : overrides;
        } catch (e) {
          console.warn('Calin Outfits: invalid inline config');
        }
      }

      this._config = base;
      return this._config;
    }

    _deepMerge(target, source) {
      const out = { ...target };
      Object.keys(source).forEach((key) => {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          out[key] = this._deepMerge(target[key] || {}, source[key]);
        } else if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          out[key] = source[key];
        }
      });
      return out;
    }

    _normalizeItems(items) {
      return items.map((item) => {
        let points = item.points;
        if (!Array.isArray(points)) {
          points = String(item.pointsText || points || '')
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
        }

        const thumbPreset = item.thumbPreset || item.id || 'none';

        return {
          ...item,
          thumbPreset,
          points
        };
      });
    }

    _escapeAttr(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    /**
     * Thumb priority: preset dropdown (not none) → custom SVG → image.
     */
    _thumbHtml(item, variant) {
      const preset = item.thumbPreset && item.thumbPreset !== 'none' ? item.thumbPreset : '';
      const presetInner = preset && OUTFIT_THUMBS[preset] ? OUTFIT_THUMBS[preset] : '';
      const svgMarkup = (item.thumbSvg || '').trim();
      const imageUrl = (item.thumbImage || '').trim();
      const isViewer = variant === 'viewer';
      const svgClass = isViewer ? '' : ' class="calin-sf__shirt-thumb"';
      const imgClass = isViewer ? '' : ' class="calin-sf__shirt-thumb"';
      const alt = this._escapeAttr(item.name);

      if (presetInner) {
        return `<svg${svgClass} viewBox="0 0 48 44">${presetInner}</svg>`;
      }

      if (svgMarkup) {
        if (/<svg[\s>]/i.test(svgMarkup)) {
          return svgMarkup;
        }
        return `<svg${svgClass} viewBox="0 0 48 44">${svgMarkup}</svg>`;
      }

      if (imageUrl) {
        return `<img${imgClass} src="${this._escapeAttr(imageUrl)}" alt="${alt}" width="48" height="44" loading="lazy">`;
      }

      return `<svg${svgClass} viewBox="0 0 48 44"></svg>`;
    }

    render() {
      const o = this.outfits;
      const label = this.dataset.label && this.dataset.label !== '' ? this.dataset.label : o.label;
      const heading = this.dataset.heading && this.dataset.heading !== '' ? this.dataset.heading : o.heading;
      const sub = this.dataset.sub && this.dataset.sub !== '' ? this.dataset.sub : o.sub;

      this.innerHTML = `
        <div class="calin-sf calin-outfits">
          <div class="calin-sf__panel is-active" data-panel="outfits">
            ${label ? `<p class="calin-sf__label">${label}</p>` : ''}
            ${heading ? `<h3 class="calin-sf__heading">${heading}</h3>` : ''}
            ${sub ? `<p class="calin-sf__sub">${sub}</p>` : ''}
            <div class="calin-sf__shirt-grid" data-outfit-grid></div>
            <div class="calin-sf__viewer">
              <div class="calin-sf__viewer-visual" data-outfit-visual></div>
              <div class="calin-sf__viewer-info">
                <span class="calin-sf__tag" data-outfit-tag></span>
                <p class="calin-sf__viewer-title" data-outfit-title></p>
                <p class="calin-sf__viewer-verdict" data-outfit-verdict></p>
                <p class="calin-sf__viewer-desc" data-outfit-desc></p>
                <div class="calin-sf__verdict-list" data-outfit-points></div>
              </div>
            </div>
          </div>
        </div>`;
    }

    renderOutfits() {
      const grid = this.querySelector('[data-outfit-grid]');
      if (!grid) return;
      const items = this.outfits.items;
      grid.innerHTML = items
        .map(
          (item, i) => `
        <button type="button" class="calin-sf__shirt-btn${i === this.state.outfitIdx ? ' is-active' : ''}" data-outfit-idx="${i}">
          ${this._thumbHtml(item, 'grid')}
          <span class="calin-sf__shirt-name">${item.name || ''}</span>
        </button>`
        )
        .join('');
      grid.querySelectorAll('[data-outfit-idx]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.outfitIdx = Number(btn.dataset.outfitIdx);
          this.renderOutfits();
        });
      });

      const item = items[this.state.outfitIdx];
      if (!item) return;

      const visual = this.querySelector('[data-outfit-visual]');
      if (visual) visual.innerHTML = this._thumbHtml(item, 'viewer');

      const tagEl = this.querySelector('[data-outfit-tag]');
      const titleEl = this.querySelector('[data-outfit-title]');
      const verdictEl = this.querySelector('[data-outfit-verdict]');
      const descEl = this.querySelector('[data-outfit-desc]');
      const pointsEl = this.querySelector('[data-outfit-points]');

      if (tagEl) tagEl.textContent = item.tag || '';
      if (titleEl) titleEl.textContent = item.name || '';
      if (verdictEl) verdictEl.textContent = item.verdict || '';
      if (descEl) descEl.textContent = item.desc || '';
      if (pointsEl) {
        pointsEl.innerHTML = (item.points || [])
          .map((p) => `<div class="calin-sf__verdict-item">${CHECK_ICON}<span>${p}</span></div>`)
          .join('');
      }
    }
  }

  if (!customElements.get('calin-outfits-viewer')) {
    customElements.define('calin-outfits-viewer', CalinOutfitsViewer);
  }
})();
